"use client"

import { useTranslations } from "next-intl"

import { DiscHeartIcon, HeartGlyphIcon, IconButton } from "@/components/ui"
import { useSetWishlisted, useWishlistIds } from "@/platform/shopper/use-wishlist"

interface WishlistHeartProps {
  productId: string
  // "card" is the product card's 30px frost disc; "stage" is the product page gallery's, 36px on a
  // phone and a 50px drawn disc from @3xl.
  size?: "card" | "stage"
  className?: string
}

const FRAME = {
  card: "backdrop-blur-frost size-7.5 border",
  stage: "backdrop-blur-frost-lg rim-frost-lg size-9 @3xl:size-12.5",
}

export const WishlistHeart = ({ productId, size = "card", className }: WishlistHeartProps) => {
  const t = useTranslations("marketplace")
  const { savedIds } = useWishlistIds()
  const { mutate, variables, isPending, isError } = useSetWishlisted()
  const mine = variables?.productId === productId
  const saved = isPending && mine ? variables.save : savedIds.has(productId)
  const failed = isError && mine
  const tint = saved ? "text-fg-danger" : ""
  const edge = failed ? "border-line-danger border" : size === "card" ? "border-frost-rim" : ""

  return (
    <>
      <IconButton
        variant="ghost"
        aria-label={saved ? t("product.removeFavorite") : t("product.saveFavorite")}
        aria-pressed={saved}
        aria-busy={isPending && mine ? true : undefined}
        className={`bg-frost text-fg-inverted hover:bg-frost ${FRAME[size]} ${edge} ${className ?? ""}`}
        onClick={() => mutate({ productId, save: !saved })}
        icon={
          size === "card" ? (
            <HeartGlyphIcon size={18} filled={saved} className={`shrink-0 ${tint}`} />
          ) : (
            <>
              <HeartGlyphIcon size={18} filled={saved} className={`shrink-0 @3xl:hidden ${tint}`} />
              <DiscHeartIcon size={50} filled={saved} className={`hidden shrink-0 @3xl:block ${tint}`} />
            </>
          )
        }
      />
      <span role="status" className="sr-only">
        {failed ? t("product.favoriteFailed") : ""}
      </span>
    </>
  )
}
